import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Scan, History, Crown, User } from 'lucide-react';

/**
 * Premium floating bottom navigation with neon glow.
 * Center button (Scan) is the highlighted CTA to start a new reading.
 */
const FloatingNav = ({ disabledScan = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const isActive = (route) => path === route;

  return (
    <nav
      data-testid="floating-nav"
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-24px)] max-w-[440px]"
    >
      <div
        className="relative flex items-center justify-around px-2 py-2 rounded-full"
        style={{
          background: 'rgba(10, 15, 20, 0.85)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(34, 211, 238, 0.2)',
          boxShadow:
            '0 18px 50px rgba(0,0,0,0.45), 0 0 40px rgba(34,211,238,0.10)',
        }}
      >
        {/* Home */}
        <NavItem
          to="/dashboard"
          icon={Home}
          label="Início"
          active={isActive('/dashboard')}
          testid="nav-home"
        />

        {/* History */}
        <NavItem
          to="/historico"
          icon={History}
          label="Histórico"
          active={isActive('/historico')}
          testid="nav-history"
        />

        {/* Center Scan CTA */}
        <button
          onClick={() => !disabledScan && navigate('/nova-leitura')}
          disabled={disabledScan}
          data-testid="nav-scan"
          className={`relative -mt-8 w-16 h-16 rounded-full flex items-center justify-center transition-all ${
            disabledScan ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
          }`}
          style={{
            background:
              'linear-gradient(135deg, #0F766E 0%, #22D3EE 100%)',
            boxShadow:
              '0 8px 30px rgba(34,211,238,0.5), inset 0 1px 0 rgba(255,255,255,0.2)',
          }}
        >
          {!disabledScan && (
            <motion.span
              className="absolute inset-0 rounded-full"
              animate={{
                boxShadow: [
                  '0 0 0 0 rgba(34,211,238,0.7)',
                  '0 0 0 16px rgba(34,211,238,0)',
                ],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          )}
          <Scan className="w-7 h-7 text-white" />
        </button>

        {/* Plans */}
        <NavItem
          to="/planos"
          icon={Crown}
          label="Planos"
          active={isActive('/planos')}
          testid="nav-plans"
          gold
        />

        {/* Account */}
        <NavItem
          to="/conta"
          icon={User}
          label="Conta"
          active={isActive('/conta')}
          testid="nav-account"
        />
      </div>
    </nav>
  );
};

const NavItem = ({ to, icon: Icon, label, active, testid, gold }) => {
  const activeColor = gold ? '#D4A256' : '#22D3EE';
  return (
    <Link
      to={to}
      data-testid={testid}
      aria-current={active ? 'true' : 'false'}
      className="flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 rounded-xl transition-colors"
      style={{ color: active ? activeColor : 'rgba(255,255,255,0.55)' }}
    >
      <Icon className="w-5 h-5" />
      <span className="text-[10px] font-semibold tracking-wide">{label}</span>
    </Link>
  );
};

export default FloatingNav;
